import React, { useState, useEffect, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CampaignIcon from "@mui/icons-material/Campaign";
import PollIcon from "@mui/icons-material/Poll";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useFetchActivePanelsQuery,
  useFetchSystemSummaryQuery,
  useFetchQuestionnairesQuery,
  useCreatePanelMutation,
  useDeletePanelMutation,
} from "../../store";
import { useFetchMeQuery } from "../../store";
import { PERSONEL } from "../../roles";

const AUTOPLAY_INTERVAL = 5500;

// ─── Slide Theme Configs ────────────────────────────────────────────────────
const THEMES = {
  announcement: {
    gradient: "linear-gradient(135deg, #1e293b 0%, #334155 55%, #5c1212 100%)",
    accent: "#f59e0b",
    glow: "rgba(245,158,11,0.18)",
    tagColor: "#fbbf24",
    tagBg: "rgba(251,191,36,0.12)",
  },
  ai: {
    gradient: "linear-gradient(135deg, #3b0764 0%, #5b21b6 55%, #8B1A1A 100%)",
    accent: "#c084fc",
    glow: "rgba(192,132,252,0.28)",
    tagColor: "#e879f9",
    tagBg: "rgba(232,121,249,0.12)",
  },
  questionnaire: {
    gradient: "linear-gradient(135deg, #052e16 0%, #15803d 55%, #8B1A1A 100%)",
    accent: "#34d399",
    glow: "rgba(52,211,153,0.22)",
    tagColor: "#34d399",
    tagBg: "rgba(52,211,153,0.12)",
  },
  manager: {
    gradient: "linear-gradient(135deg, #18181b 0%, #3f3f46 55%, #4a1010 100%)",
    accent: "#fb923c",
    glow: "rgba(251,146,60,0.18)",
    tagColor: "#fb923c",
    tagBg: "rgba(251,146,60,0.12)",
  },
};

// ─── Helper: Decorative background circles ───────────────────────────────────
const DecorCircles = () => (
  <>
    <Box sx={{ position: "absolute", top: -55, right: -55, width: 230, height: 230, borderRadius: "50%", background: "rgba(255,255,255,0.035)", zIndex: 1, pointerEvents: "none" }} />
    <Box sx={{ position: "absolute", bottom: -80, left: "20%", width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.025)", zIndex: 1, pointerEvents: "none" }} />
  </>
);

// ─── Helper: Loading skeleton ─────────────────────────────────────────────────
const LoadingState = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
    <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.55)" }} />
    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>Yükleniyor...</Typography>
  </Box>
);

// ─── Helper: Progress Bar ────────────────────────────────────────────────────
const ProgressBar = ({ accent, slideKey, isPaused }) => {
  if (isPaused) return null;
  return (
    <Box sx={{ position: "absolute", bottom: 0, left: 0, height: 3, bgcolor: "rgba(255,255,255,0.08)", width: "100%", zIndex: 10 }}>
      <motion.div
        key={`pb-${slideKey}`}
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        transition={{ duration: AUTOPLAY_INTERVAL / 1000, ease: "linear" }}
        style={{ height: "100%", background: accent, borderRadius: 2 }}
      />
    </Box>
  );
};

// ─── Helper: Icon Bubble ──────────────────────────────────────────────────────
const IconBubble = ({ children, glow }) => (
  <Box sx={{
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    justifyContent: "center",
    width: 88,
    height: 88,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
    backdropFilter: "blur(14px)",
    border: "1.5px solid rgba(255,255,255,0.13)",
    color: "#fff",
    flexShrink: 0,
    boxShadow: `0 6px 36px ${glow}`,
  }}>
    {children}
  </Box>
);

// ─── Helper: Standard Slide Layout ────────────────────────────────────────────
const SlideContent = ({ theme, tag, tagIcon, icon, title, desc, badge }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 4 }, width: "100%" }}>
    <IconBubble glow={theme.glow}>{icon}</IconBubble>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Chip
          icon={tagIcon}
          label={tag}
          size="small"
          sx={{
            color: theme.tagColor,
            bgcolor: theme.tagBg,
            border: `1px solid ${theme.tagColor}44`,
            fontWeight: 700,
            fontSize: 11,
            height: 24,
            "& .MuiChip-icon": { color: theme.tagColor },
          }}
        />
        {badge}
      </Box>
      <Typography
        variant="h5"
        fontWeight={800}
        sx={{ color: "#fff", mb: 1, lineHeight: 1.2, fontSize: { xs: "1.05rem", sm: "1.3rem" }, letterSpacing: -0.3 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "rgba(255,255,255,0.68)",
          lineHeight: 1.7,
          fontSize: { xs: "0.78rem", sm: "0.875rem" },
          maxWidth: "100%",
        }}
      >
        {desc}
      </Typography>
    </Box>
  </Box>
);

// ─── Slide Wrapper (shared card container) ────────────────────────────────────
const SlideWrapper = ({ theme, children, isPaused, slideKey }) => (
  <Box sx={{
    background: theme.gradient,
    borderRadius: 4,
    position: "relative",
    overflow: "hidden",
    boxShadow: `0 10px 52px ${theme.glow}`,
    minHeight: { xs: 200, sm: 210 },
  }}>
    <DecorCircles />
    <Box sx={{ position: "relative", zIndex: 2, p: { xs: 3, sm: 4, md: 5 }, pr: { xs: 7, sm: 8 } }}>
      {children}
    </Box>
    <ProgressBar accent={theme.accent} slideKey={slideKey} isPaused={isPaused} />
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AnnouncementSlider = () => {
  const { data: user } = useFetchMeQuery();
  const isManager = user?.roleId !== PERSONEL;

  const { data: panelsData, isLoading: panelsLoading } = useFetchActivePanelsQuery();
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useFetchSystemSummaryQuery();
  const { data: questData, isLoading: questLoading } = useFetchQuestionnairesQuery();
  const [createPanel, { isLoading: createLoading }] = useCreatePanelMutation();
  const [deletePanel] = useDeletePanelMutation();

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  // Form state
  const [newTitle, setNewTitle] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [addSuccess, setAddSuccess] = useState(false);

  // Drag-and-drop state for announcement reorder
  const [orderedPanels, setOrderedPanels] = useState([]);
  const dragIndexRef = useRef(null);

  useEffect(() => {
    const panels = panelsData?.data || [];
    if (panels.length > 0) setOrderedPanels(panels);
  }, [panelsData]);

  const totalSlides = isManager ? 4 : 3;

  const goTo = useCallback((index, dir) => {
    setDirection(dir);
    setCurrent(((index % totalSlides) + totalSlides) % totalSlides);
  }, [totalSlides]);

  const next = useCallback(() => goTo(current + 1, 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1, -1), [current, goTo]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [next, isPaused]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = (index) => { dragIndexRef.current = index; };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e, dropIndex) => {
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === dropIndex) return;
    const list = [...orderedPanels];
    const [moved] = list.splice(fromIndex, 1);
    list.splice(dropIndex, 0, moved);
    setOrderedPanels(list);
    dragIndexRef.current = null;
  };

  // ── Create handler ────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await createPanel({ title: newTitle, message: newMessage, panelType: 1, selectedTeamIds: [] });
    setNewTitle("");
    setNewMessage("");
    setAddSuccess(true);
    setTimeout(() => setAddSuccess(false), 3000);
  };

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await deletePanel(id);
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const firstPanel = orderedPanels[0];
  const now = new Date();
  const activeQuests = (questData?.data || []).filter(
    (q) => q.endDate && new Date(q.endDate) > now
  );

  // ── Animation variants ────────────────────────────────────────────────────
  const variants = {
    enter: (dir) => ({ x: dir > 0 ? "55%" : "-55%", opacity: 0, scale: 0.97 }),
    center: { x: 0, opacity: 1, scale: 1, transition: { duration: 0.46, ease: [0.4, 0, 0.2, 1] } },
    exit:  (dir) => ({ x: dir > 0 ? "-55%" : "55%", opacity: 0, scale: 0.97, transition: { duration: 0.36, ease: [0.4, 0, 0.2, 1] } }),
  };

  // ── Theme for current slide ───────────────────────────────────────────────
  const themeMap = ["announcement", "ai", "questionnaire", "manager"];
  const currentTheme = THEMES[themeMap[current]] || THEMES.announcement;

  // ── Render individual slides ──────────────────────────────────────────────
  const renderSlide = (idx) => {
    // SLIDE 0 — Announcements
    if (idx === 0) {
      const t = THEMES.announcement;
      return (
        <SlideWrapper theme={t} isPaused={isPaused} slideKey={`ann-${idx}`}>
          {panelsLoading ? <LoadingState /> : firstPanel ? (
            <SlideContent
              theme={t}
              tag="Duyuru"
              tagIcon={<CampaignIcon sx={{ fontSize: 14 }} />}
              icon={<CampaignIcon sx={{ fontSize: 50 }} />}
              title={firstPanel.title}
              desc={firstPanel.message || "Detaylar için yöneticinizle iletişime geçin."}
            />
          ) : (
            <SlideContent
              theme={t}
              tag="Duyuru"
              tagIcon={<CampaignIcon sx={{ fontSize: 14 }} />}
              icon={<CampaignIcon sx={{ fontSize: 50 }} />}
              title="Henüz Duyuru Yok"
              desc="Yöneticiniz tarafından yeni duyurular eklendiğinde burada görünecek."
            />
          )}
        </SlideWrapper>
      );
    }

    // SLIDE 1 — AI Summary
    if (idx === 1) {
      const t = THEMES.ai;
      return (
        <SlideWrapper theme={t} isPaused={isPaused} slideKey={`ai-${idx}`}>
          {summaryLoading ? <LoadingState /> : (
            <SlideContent
              theme={t}
              tag="Yapay Zeka Özeti"
              tagIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              icon={<AutoAwesomeIcon sx={{ fontSize: 50 }} />}
              title="Günlük Sistem Özeti"
              desc={summaryData?.data || summaryError?.data?.message || "Özet şu an kullanılamıyor. Backend bağlantısı gerektirir."}
            />
          )}
        </SlideWrapper>
      );
    }

    // SLIDE 2 — Questionnaires
    if (idx === 2) {
      const t = THEMES.questionnaire;
      const count = activeQuests.length;
      const surveyTitle = count > 0 ? `${count} Aktif Anket` : "Aktif Anket Yok";
      const surveyDesc = count > 0
        ? activeQuests.slice(0, 2).map((q) => `• ${q.name}`).join("   ") +
          (count > 2 ? `   ve ${count - 2} anket daha...` : "")
        : "Şu an tamamlanmanızı bekleyen aktif bir anket bulunmuyor.";
      return (
        <SlideWrapper theme={t} isPaused={isPaused} slideKey={`q-${idx}`}>
          {questLoading ? <LoadingState /> : (
            <SlideContent
              theme={t}
              tag="Anketler"
              tagIcon={<PollIcon sx={{ fontSize: 14 }} />}
              icon={<PollIcon sx={{ fontSize: 50 }} />}
              title={surveyTitle}
              desc={surveyDesc}
              badge={count > 0 ? (
                <Chip
                  label={`${count} aktif`}
                  size="small"
                  sx={{ bgcolor: t.tagBg, color: t.tagColor, fontWeight: 700, fontSize: 10, height: 20 }}
                />
              ) : null}
            />
          )}
        </SlideWrapper>
      );
    }

    // SLIDE 3 — Manager Panel (only when isManager)
    if (idx === 3 && isManager) {
      const t = THEMES.manager;
      return (
        <Box sx={{
          background: t.gradient,
          borderRadius: 4,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 10px 52px ${t.glow}`,
          minHeight: { xs: 220, sm: 240 },
        }}>
          <DecorCircles />
          <Box sx={{ position: "relative", zIndex: 2, p: { xs: 2.5, sm: 3, md: 4 } }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 38, height: 38, borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.07)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.1)", color: t.accent,
              }}>
                <TuneIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#fff", lineHeight: 1.1 }}>
                  Duyuru Yönetimi
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
                  Yeni duyuru ekle · Sürükle-bırak ile sırala
                </Typography>
              </Box>
            </Box>

            {/* Two-column layout */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {/* ── Add Announcement Form ── */}
              <Box sx={{
                bgcolor: "rgba(255,255,255,0.055)", backdropFilter: "blur(10px)",
                borderRadius: 3, p: 2, border: "1px solid rgba(255,255,255,0.09)",
              }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 0.9, display: "block", mb: 1.5 }}>
                  Yeni Duyuru
                </Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="Başlık *"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  sx={{
                    mb: 1,
                    "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2, color: "#fff", fontSize: "0.85rem" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.13)" },
                    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.3)", opacity: 1 },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.25)" },
                  }}
                />
                <TextField
                  fullWidth size="small"
                  placeholder="Mesaj (opsiyonel)"
                  multiline rows={2}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  sx={{
                    mb: 1.5,
                    "& .MuiInputBase-root": { bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2, color: "#fff", fontSize: "0.85rem" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.13)" },
                    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.3)", opacity: 1 },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.25)" },
                  }}
                />
                <Button
                  fullWidth size="small"
                  variant="contained"
                  onClick={handleCreate}
                  disabled={createLoading || !newTitle.trim()}
                  startIcon={addSuccess ? <CheckCircleOutlineIcon /> : <AddCircleOutlineIcon />}
                  sx={{
                    bgcolor: addSuccess ? "#10b981" : t.accent,
                    color: "#18181b",
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.82rem",
                    "&:hover": { bgcolor: addSuccess ? "#059669" : "#ea8c00" },
                    "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
                  }}
                >
                  {addSuccess ? "Eklendi!" : createLoading ? "Ekleniyor..." : "Duyuru Ekle"}
                </Button>
              </Box>

              {/* ── Reorder List ── */}
              <Box sx={{
                bgcolor: "rgba(255,255,255,0.055)", backdropFilter: "blur(10px)",
                borderRadius: 3, p: 2, border: "1px solid rgba(255,255,255,0.09)",
              }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 0.9, display: "block", mb: 1.5 }}>
                  Duyurular ({orderedPanels.length})
                </Typography>
                {orderedPanels.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.3)", textAlign: "center", py: 2, fontSize: "0.8rem" }}>
                    Henüz duyuru eklenmedi
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 138, overflowY: "auto", pr: 0.5,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                    "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.15)", borderRadius: 2 },
                  }}>
                    {orderedPanels.map((panel, i) => (
                      <Box
                        key={panel.id}
                        draggable
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, i)}
                        sx={{
                          display: "flex", alignItems: "center", gap: 0.8,
                          p: "6px 8px", mb: 0.7, borderRadius: 1.5,
                          bgcolor: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.07)",
                          cursor: "grab",
                          transition: "background 0.15s",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.09)" },
                          "&:active": { cursor: "grabbing" },
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.25)", flexShrink: 0 }} />
                        <Typography noWrap variant="caption" sx={{ color: "rgba(255,255,255,0.85)", flex: 1, fontWeight: 600, fontSize: "0.78rem" }}>
                          {panel.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(panel.id, e)}
                          sx={{ color: "#f87171", p: 0.25, "&:hover": { bgcolor: "rgba(248,113,113,0.12)" }, flexShrink: 0 }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ mb: 5 }}>
      {/* Section header + dots */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary" }}>
          Duyurular &amp; Haberler
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          {Array.from({ length: totalSlides }).map((_, i) => (
            <Box
              key={i}
              onClick={() => goTo(i, i > current ? 1 : -1)}
              sx={{
                width: i === current ? 22 : 7,
                height: 7,
                borderRadius: 99,
                bgcolor: i === current ? currentTheme.accent : "rgba(0,0,0,0.15)",
                cursor: "pointer",
                transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
                "&:hover": { opacity: 0.7 },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Slider area */}
      <Box
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        sx={{ position: "relative", userSelect: "none" }}
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={current}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {renderSlide(current)}
          </motion.div>
        </AnimatePresence>

        {/* ← Prev arrow */}
        <IconButton
          onClick={prev}
          size="small"
          sx={{
            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
            zIndex: 10, color: "#fff",
            bgcolor: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.13)",
            width: 34, height: 34,
            "&:hover": { bgcolor: "rgba(0,0,0,0.48)" },
            transition: "background 0.2s",
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
        </IconButton>

        {/* → Next arrow */}
        <IconButton
          onClick={next}
          size="small"
          sx={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            zIndex: 10, color: "#fff",
            bgcolor: "rgba(0,0,0,0.28)", backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.13)",
            width: 34, height: 34,
            "&:hover": { bgcolor: "rgba(0,0,0,0.48)" },
            transition: "background 0.2s",
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default AnnouncementSlider;
